import type { TenantSite } from '../types/tenant-config'

/**
 * Builds the base URL for a tenant site.
 * Handles both domain-per-country (VistaPrint) and path-prefix (National Pen) patterns.
 *
 * Examples:
 * - VistaPrint IE: { domain: 'www.vistaprint.ie' } → 'https://www.vistaprint.ie/'
 * - National Pen US: { domain: 'pens.experience.cimpress.io', pathPrefix: '/us' } → 'https://pens.experience.cimpress.io/us/'
 */
export function buildSiteUrl(site: TenantSite): string {
  const prefix = site.pathPrefix || ''
  return `https://${site.domain}${prefix}/`
}

/**
 * Builds a locale-specific URL for a tenant site.
 * Appends the language prefix when the locale is not the primary locale
 * (or when omitPrimaryPrefix is false).
 *
 * Examples:
 * - VistaPrint CA, locale 'fr-CA': 'https://www.vistaprint.ca/fr/'
 * - VistaPrint CA, locale 'en-CA' (primary, omitPrimaryPrefix=true): 'https://www.vistaprint.ca/'
 * - National Pen IE, locale 'en-IE' (primary, omitPrimaryPrefix=true): 'https://pens.experience.cimpress.io/ie/'
 */
export function buildLocaleUrl(site: TenantSite, locale: string): string {
  const baseUrl = buildSiteUrl(site)
  const isPrimaryLocale = locale.toLowerCase() === site.locales.primary.toLowerCase()

  // If it's the primary locale and we omit the prefix, return base URL
  if (isPrimaryLocale && site.locales.omitPrimaryPrefix) {
    return baseUrl
  }

  // Extract language code from locale (e.g., 'fr-CA' → 'fr')
  const languageCode = locale.split('-')[0]?.toLowerCase() ?? ''
  return `${baseUrl}${languageCode}/`
}

/**
 * Finds the site matching a given locale in the tenant config sites array.
 * A site matches if the locale is in its supported locales list.
 */
export function findSiteByLocale(sites: TenantSite[], locale: string): TenantSite | undefined {
  const normalizedLocale = locale.toLowerCase()
  return sites.find((site) => site.locales.supported.some((l) => l.toLowerCase() === normalizedLocale))
}
